import { alterElementOnImgErr, StyledImgWrapper } from "@components/common/Common.styles";
import { EcoaParagraph } from "@components/common/EcoaParagraph";
import { EcoaContent } from "@components/common/EconContent";
import { FontController } from "@components/common/FontController";
import { getLaStringValue, isEcoaImgUrlSpec } from "@lib/functions/common";
import { arrayify } from "@maven-helper/functions/Misc.function";
import React, { useEffect, useMemo, useRef } from "react";
import styled from "@emotion/styled";
import { LaString, LoLang } from "maven-lib/dist/consts";
import { Footer } from "@layouts/Footer";
import { EcoaFontSize, useLangContext } from "@layouts/home/LangContext";
import {
  CoStringColor,
  EcoaConditionalNoticeSpec,
  EcoaImgDisplaySpec,
  EcoaImgUrlSpec,
  EcoaNoticeContentSpec,
  EcoaNoticeText,
  EcoaNoticeTextStyleType,
  SentenceColorType,
  StudySpec,
} from "maven-lib/dist/consts/edc/crf";
import { EcoaQuestionnaireData } from "maven-lib/dist/dto/entity/ecoaVisit";
import { LOCALE_LANG_KEY } from "maven-lib/dist/states";
import { HbButton } from "@maven-surface/components/base/HbButton";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

type EcoaNoticeContentSpecForRender = Omit<EcoaNoticeContentSpec, "img"> & { img?: EcoaImgDisplaySpec & EcoaImgUrlSpec };

interface VisitEntranceProps {
  studySpec: StudySpec;
  folderId: string;
  visitId: string;
  visitSeq: number;
  crfPageId: string;
  crfPageSeq: number;
  questionnaire: EcoaQuestionnaireData;
  showFontController: boolean;
  noticeVerticalPosition?: "TOP" | "CENTER" | "BOTTOM";
}

const isConditionalNoticeSpec = (
  spec: Array<EcoaNoticeContentSpec> | Array<EcoaConditionalNoticeSpec>
): spec is Array<EcoaConditionalNoticeSpec> => {
  return "target" in spec[spec.length - 1];
};

export function VisitEntrance(props: VisitEntranceProps) {
  const { studySpec, folderId, visitId, visitSeq, crfPageId, crfPageSeq, questionnaire, showFontController, noticeVerticalPosition } =
    props;
  const { pushPath, query } = useRoute();
  const { fontSize, lang } = useLangContext();
  const noticeContainerRef = useRef<HTMLDivElement>(null);

  const agreeStartButtonTitle = studySpec.config?.ecoa?.buttonTitle?.agreeAndStart;

  const getVisitEntranceButtonText = (buttonTitle?: LaString): string => {
    if (buttonTitle) {
      return (typeof buttonTitle === "string" && buttonTitle) || getLaStringValue(buttonTitle, lang) || "";
    } else {
      return MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_AGREE_AND_START);
    }
  };

  const noticeContentsForRender: Array<EcoaNoticeContentSpecForRender> = useMemo(() => {
    const noticeContents = (() => {
      if (!Array.isArray(questionnaire.notice)) {
        return questionnaire.notice.contents;
      }

      if (!questionnaire.notice.length) {
        return [];
      }

      if (isConditionalNoticeSpec(questionnaire.notice)) {
        return (
          questionnaire.notice.find((noticeItem) => {
            if (!noticeItem) return false;

            const { folderId: fId, visitId: vId, visitSeq: vSeq, crfPageId: cpId, crfPageSeq: cpSeq } = noticeItem.target;

            return (
              ((fId && typeof fId === "object" && fId.reserved === "ANY") || fId === folderId) &&
              ((vId && typeof vId === "object" && vId.reserved === "ANY") || vId === visitId) &&
              ((vSeq && typeof vSeq === "object" && vSeq.reserved === "ANY") || vSeq === visitSeq) &&
              ((cpId && typeof cpId === "object" && cpId.reserved === "ANY") || cpId === crfPageId) &&
              ((cpSeq && typeof cpSeq === "object" && cpSeq.reserved === "ANY") || cpSeq === crfPageSeq || !cpSeq)
            );
          })?.contents || []
        );
      } else {
        return questionnaire.notice;
      }
    })();

    return noticeContents.reduce<Array<EcoaNoticeContentSpecForRender>>((acc, cur) => {
      if (cur.img && !isEcoaImgUrlSpec(cur.img)) {
        acc.push({ ...cur, img: cur.img ? { ...cur.img, url: "" } : undefined });
      } else {
        acc.push({ ...cur, img: cur.img ? { ...cur.img, url: cur.img.url } : undefined });
      }

      return acc;
    }, []);
  }, [crfPageId, crfPageSeq, folderId, questionnaire.notice, visitId, visitSeq]);

  const buttonSize = fontSize === "LARGE" ? "4X-LARGE" : fontSize === "MEDIUM" ? "2X-LARGE" : undefined;
  const localStorageLanguage = localStorage.getItem(LOCALE_LANG_KEY) as LoLang;

  // NOTE: container 영역이 height를 100%줘도 꽉차지 않기때문에 noticeVerticalPosition 이 TOP이 아닐때 notice height가 '컨텐츠 영역' 보다 작다면 container height를 '컨텐츠 영역'만큼 설정
  useEffect(() => {
    if (noticeVerticalPosition !== "TOP") {
      if (noticeContainerRef?.current) {
        noticeContainerRef?.current?.style.setProperty("height", "100%");

        const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
        const topNavHeight = 190;
        const footerButtonHeight = 50;
        const contentArea = vh - topNavHeight - footerButtonHeight;

        if (noticeContainerRef?.current?.offsetHeight < contentArea) {
          noticeContainerRef?.current?.style.setProperty("height", `calc(100vh - ${topNavHeight}px - ${footerButtonHeight}px)`);
        }
      }
    }
  }, [fontSize, noticeVerticalPosition]);

  return (
    <StyledVisitEntranceContainer ref={noticeContainerRef} verticalPosition={noticeVerticalPosition}>
      {showFontController && <FontController lang={localStorageLanguage} />}
      <EcoaContent showController={showFontController}>
        {noticeContentsForRender.map((content, noticeIdx) => {
          const { img, text, color, calcRefFontSize, align, bold } = content;

          return (
            <React.Fragment key={`notice-${noticeIdx}`}>
              {img?.position === "TOP" && (
                <StyledImgWrapper>
                  <img
                    alt=""
                    style={{ width: "100%", objectFit: "contain" }}
                    src={img.url}
                    onError={(e) => (e.currentTarget.outerHTML = alterElementOnImgErr)}
                  />
                </StyledImgWrapper>
              )}
              <NoticeParagraph text={text} color={color} fontSize={fontSize} calcRefFontSize={calcRefFontSize} align={align} bold={bold} />
              {img?.position === "BOTTOM" && (
                <StyledImgWrapper>
                  <img
                    alt=""
                    style={{ width: "100%", objectFit: "contain" }}
                    src={img.url}
                    onError={(e) => (e.currentTarget.outerHTML = alterElementOnImgErr)}
                  />
                </StyledImgWrapper>
              )}
            </React.Fragment>
          );
        })}
      </EcoaContent>
      <Footer>
        <HbButton
          onClick={() => {
            const queryParams = query.from ? { from: String(query.from) } : undefined;
            pushPath(`/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}/1`, queryParams);
          }}
          text={getVisitEntranceButtonText(agreeStartButtonTitle)}
          size={buttonSize}
          iconRight="ChromeBackMirrored"
        />
      </Footer>
    </StyledVisitEntranceContainer>
  );
}

interface StyledVisitEntranceContainerProps {
  verticalPosition?: "TOP" | "CENTER" | "BOTTOM";
}

const StyledVisitEntranceContainer = styled.div<StyledVisitEntranceContainerProps>`
  width: 100%;
  height: 100%;

  display: flex;
  align-items: ${({ verticalPosition }) =>
    verticalPosition === "CENTER" ? "center" : verticalPosition === "BOTTOM" ? "flex-end" : "flex-start"};
  overflow: auto;

  .app-hybrid-content-wrapper {
    display: flex;
    flex-direction: column;
  }
`;

type ParagraphOptions = {
  text: EcoaNoticeText;
  color: CoStringColor | SentenceColorType;
  fontSize: EcoaFontSize;
  bold?: boolean;
  calcRefFontSize?: number;
  align?: "LEFT" | "CENTER" | "RIGHT";
};

const NoticeParagraph = ({ text, color, fontSize, calcRefFontSize, bold, align = "CENTER" }: ParagraphOptions) => {
  const arrayifyText = arrayify(text);

  const paragraphSize = fontSize === "LARGE" ? "4X-LARGE" : fontSize === "MEDIUM" ? "2X-LARGE" : undefined;
  const calcFontSize = calcRefFontSize ?? 0;

  const convertParagraphColor = (color: CoStringColor | SentenceColorType) => {
    switch (color) {
      case "RED":
        return "DANGER";
      case "GRAY":
        return "GRAY";
      case "BLUE":
        return "PRIMARY";
      default:
        return undefined;
    }
  };

  const convertSpanColor = (color: CoStringColor | SentenceColorType) => {
    if (!color || typeof color === "string") return undefined;
    if (color.type === "HEX") return color.value;
  };

  return (
    <EcoaParagraph
      align={align}
      color={convertParagraphColor(color)}
      size={paragraphSize}
      style={{ lineHeight: "normal", marginTop: 0 }}
      bold={bold}
    >
      {arrayifyText.map((text, index) => {
        if (typeof text === "string") {
          return (
            <StyledNoticeSpan key={index} style={{ fontSize: `calc(1em + ${calcFontSize}px)` }} color={convertSpanColor(color)}>
              {text}
            </StyledNoticeSpan>
          );
        }

        const { word, underline, bold, textStyleType } = text;

        return (
          <StyledNoticeSpan
            key={index}
            style={{ fontSize: `calc(1em + ${calcFontSize}px)` }}
            className={typeof text.color === "string" ? `--color-${(text.color || "default").toLowerCase()}` : ""}
            color={convertSpanColor(text.color)}
            underline={underline}
            bold={bold}
          >
            <RenderScript word={word} textStyleType={textStyleType} />
          </StyledNoticeSpan>
        );
      })}
    </EcoaParagraph>
  );
};

const RenderScript = ({ word, textStyleType }: { word: string; textStyleType?: EcoaNoticeTextStyleType }) => {
  switch (textStyleType) {
    case "SUB":
      return <sub>{word}</sub>;
    case "SUP":
      return <sup>{word}</sup>;
    case "SMALL":
      return <small>{word}</small>;
    default:
      return <>{word}</>;
  }
};

interface StyledNoticeSpanProps {
  color?: `#${string}`;
  underline?: boolean;
  bold?: boolean;
}

const StyledNoticeSpan = styled.span<StyledNoticeSpanProps>`
  min-height: 18px;

  color: ${({ color }) => color ?? "inherit"};
  ${({ underline }) => underline && "text-decoration: underline"};
  ${({ bold }) => bold && "font-weight: bold"};
`;
